import {
  keepPreviousData,
  queryOptions,
  useMutation,
  useQueryClient,
} from "@tanstack/react-query";
import {
  deleteLlmModelPrice,
  fetchLlmModelPrices,
  saveLlmModelPrice,
  type LlmModelPriceInput,
} from "@/features/llm-usage/api/llm-model-prices.api";
import type { LlmProvider } from "@/features/llm-usage/api/llm-usage.api";
import {
  fetchLlmUsageFeatures,
  fetchLlmUsageStats,
  fetchLlmUsageTenants,
  fetchLlmUsageTimeseries,
  fetchVertexUsage,
  type LlmUsageFilters,
} from "@/features/llm-usage/api/llm-usage.api";

/**
 * Token usage is written on every LLM call but read by an operator at human
 * pace, so a rolling window goes stale quickly without needing to be live.
 */
const LLM_USAGE_STALE_TIME_MS = 30 * 1000;
/** The feature list only grows when code ships, not when traffic arrives. */
const LLM_USAGE_FEATURES_STALE_TIME_MS = 5 * 60 * 1000;
/**
 * Cloud Monitoring ingests on its own delay and bills per read, so this is
 * polled far more lazily than our own table.
 */
const VERTEX_USAGE_STALE_TIME_MS = 5 * 60 * 1000;

/** Rates change when a provider republishes them, which is a rare human act. */
const LLM_MODEL_PRICES_STALE_TIME_MS = 5 * 60 * 1000;

export const llmUsageKeys = {
  all: ["llm-usage"] as const,
  stats: (filters: LlmUsageFilters) => ["llm-usage", "stats", filters] as const,
  timeseries: (filters: LlmUsageFilters) => ["llm-usage", "timeseries", filters] as const,
  features: (windowHours?: number) => ["llm-usage", "features", windowHours ?? null] as const,
  tenants: (windowHours?: number) => ["llm-usage", "tenants", windowHours ?? null] as const,
  vertex: (windowHours?: number) => ["llm-usage", "vertex", windowHours ?? null] as const,
  prices: () => ["llm-usage", "prices"] as const,
} as const;

/**
 * `keepPreviousData` holds the last window's numbers while the next one is in
 * flight, so changing the range moves the strip's values rather than tearing it
 * down to skeletons. `isPlaceholderData` is what says they belong to the old
 * window.
 */
export function llmUsageStatsQueryOptions(filters: LlmUsageFilters) {
  return queryOptions({
    queryKey: llmUsageKeys.stats(filters),
    queryFn: () => fetchLlmUsageStats(filters),
    staleTime: LLM_USAGE_STALE_TIME_MS,
    placeholderData: keepPreviousData,
  });
}

export function llmUsageTimeseriesQueryOptions(filters: LlmUsageFilters) {
  return queryOptions({
    queryKey: llmUsageKeys.timeseries(filters),
    queryFn: () => fetchLlmUsageTimeseries(filters),
    staleTime: LLM_USAGE_STALE_TIME_MS,
    placeholderData: keepPreviousData,
  });
}

export function llmUsageFeaturesQueryOptions(windowHours?: number) {
  return queryOptions({
    queryKey: llmUsageKeys.features(windowHours),
    queryFn: () => fetchLlmUsageFeatures(windowHours),
    staleTime: LLM_USAGE_FEATURES_STALE_TIME_MS,
  });
}

/** Unlike the feature list, this grows as new orgs make their first call. */
export function llmUsageTenantsQueryOptions(windowHours?: number) {
  return queryOptions({
    queryKey: llmUsageKeys.tenants(windowHours),
    queryFn: () => fetchLlmUsageTenants(windowHours),
    staleTime: LLM_USAGE_STALE_TIME_MS,
  });
}

/**
 * The reconciliation panel. `skipGlobalError` because the route already reports
 * an unreadable Cloud Monitoring as `available: false` rather than an error —
 * a genuine transport failure here should not raise a global toast over a
 * secondary panel.
 */
export function vertexUsageQueryOptions(windowHours?: number) {
  return queryOptions({
    queryKey: llmUsageKeys.vertex(windowHours),
    queryFn: () => fetchVertexUsage(windowHours),
    staleTime: VERTEX_USAGE_STALE_TIME_MS,
    placeholderData: keepPreviousData,
    meta: { skipGlobalError: true },
  });
}

export function llmModelPricesQueryOptions() {
  return queryOptions({
    queryKey: llmUsageKeys.prices(),
    queryFn: fetchLlmModelPrices,
    staleTime: LLM_MODEL_PRICES_STALE_TIME_MS,
  });
}

/**
 * Both price writes invalidate the whole feature, not just the price list: a
 * changed rate re-costs every window on the tab, so leaving the cached stats
 * in place would show old money beside the new rate that produced it.
 */
export function useSaveLlmModelPrice() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (input: LlmModelPriceInput) => saveLlmModelPrice(input),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: llmUsageKeys.all }),
    meta: { skipGlobalError: true },
  });
}

export function useDeleteLlmModelPrice() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ provider, model }: { provider: LlmProvider; model: string }) =>
      deleteLlmModelPrice(provider, model),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: llmUsageKeys.all }),
    meta: { skipGlobalError: true },
  });
}
